import { Injectable, inject, OnDestroy } from '@angular/core';
import { Idle, DEFAULT_INTERRUPTSOURCES, InterruptSource, EventTargetInterruptSource } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { AuthService } from './auth.service';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Subscription } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class UserInactiveService implements OnDestroy {
  idleState = 'Not started';
  timedOut = false;
  authService = inject(AuthService);
  modal = inject(NzModalService);
  modalRef: any;
  private subscription!: Subscription;

  constructor(private idle: Idle, private keepalive: Keepalive) {
    this.subscription = this.authService.userLoggedInMessage$.subscribe({
      next: (message) => {
        if (message === 'loggedIn') {
          this.initInactivityTimer();
        } else if (message === 'loggedOut') {
          this.stopIdleWatch();
        }
      },
      error: (err) => console.error('Error in subscription:', err)
    });
  }

  initInactivityTimer() {
    // Set idle time (e.g., 5 minutes)
    this.idle.setIdle(1680);
    // Set timeout after idle (e.g., 1 minute)
    this.idle.setTimeout(120);
    // Override default interrupt sources
    const customSources: InterruptSource[] = [
      new EventTargetInterruptSource(document, 'click'),
      new EventTargetInterruptSource(document, 'keydown'),
      new EventTargetInterruptSource(document, 'touchstart'),
      new EventTargetInterruptSource(document, 'touchmove'),
      new EventTargetInterruptSource(document, 'mousedown'),
      new EventTargetInterruptSource(document, 'scroll'),
      new EventTargetInterruptSource(document, 'mousewheel')
    ];
    // Detect user activity
    this.idle.setInterrupts(customSources);

    this.idle.onIdleEnd.subscribe(() => this.idleState = 'Active');
    this.idle.onTimeout.subscribe(() => {
      this.sessionOut();
    });
    this.idle.onIdleStart.subscribe(() => {
      this.idleState = 'You’ve gone idle...';
      this.modalRef = this.modal.confirm({
        nzTitle: 'Session Timing Out',
        nzContent: '<b>' + this.idleState + '</b>',
        nzOkText: 'Keep me Logged In!',
        nzOkType: 'primary',
        nzOkDanger: true,
        nzOnOk: () => this.reset(),
        nzCancelText: 'Logout',
        nzOnCancel: () => this.sessionOut()
      });
    });
    this.idle.onTimeoutWarning.subscribe(countdown => {
      this.idleState = `Logging out in ${countdown} seconds!`
      this.modalRef.updateConfig({
        nzContent: this.idleState
      });
    });

    // Keepalive ping every 15 seconds
    this.keepalive.interval(15);
    this.reset();
  }

  reset() {
    this.idle.watch();
    this.idleState = 'Started';
    if(this.modalRef)
      this.modalRef.close();
    this.timedOut = false;
  }

  sessionOut() {
    this.stopIdleWatch();
    this.authService.logout('type1');
  }

  stopIdleWatch() {
    this.idleState = 'Timed out!';
    this.timedOut = true;
    this.modal.closeAll();
    this.idle.stop();
  }

  ngOnDestroy(): void {
    // Prevent memory leaks
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }
}
